import React, { useState, useEffect } from 'react';
import { invoiceService, trustService } from '../../services/api';
import { X, DollarSign, AlertCircle } from 'lucide-react';
import useModal from '../../hooks/useModal';

const AddPaymentModal = ({ invoice, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: '',
    reference_number: '',
    notes: '',
    trust_account_id: ''
  });
  const [trustAccounts, setTrustAccounts] = useState([]);

  const balanceDue = invoiceService.calculateBalance(invoice);
  const maxAmount = Math.max(0, balanceDue);

  // Modal behavior
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, onClose);

  // Fetch trust accounts when trust_funds payment method is selected
  useEffect(() => {
    if (paymentData.payment_method === 'trust_funds' && trustAccounts.length === 0) {
      trustService.getTrustAccounts({ client_id: invoice.client_id }).then(res => {
        setTrustAccounts(res.data || []);
      }).catch(() => {});
    }
  }, [paymentData.payment_method, invoice.client_id, trustAccounts.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const amount = parseFloat(paymentData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    if (amount > maxAmount) {
      setError(`Payment amount cannot exceed balance due of ${invoiceService.formatCurrency(maxAmount)}`);
      return;
    }

    try {
      setLoading(true);
      
      const submitData = {
        ...paymentData,
        amount: amount,
        payment_date: new Date(paymentData.payment_date + 'T00:00:00').toISOString(),
        trust_account_id: paymentData.trust_account_id || null
      };

      await invoiceService.addPayment(invoice.id, submitData);
      onSuccess();
    } catch (error) {
      console.error('Error adding payment:', error);
      setError(error.response?.data?.detail || 'Error adding payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAmountChange = (value) => {
    // Only allow positive numbers with up to 2 decimal places
    const sanitized = value.replace(/[^0-9.]/g, '');
    const parts = sanitized.split('.');
    if (parts.length > 2) return; // Prevent multiple decimal points
    if (parts[1] && parts[1].length > 2) return; // Limit to 2 decimal places
    
    setPaymentData(prev => ({ ...prev, amount: sanitized }));
  };

  const setFullAmount = () => {
    setPaymentData(prev => ({ ...prev, amount: maxAmount.toFixed(2) }));
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <DollarSign className="w-6 h-6 text-green-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Record Payment</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Invoice Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Invoice:</span>
              <span className="font-medium">{invoice.invoice_number}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Total Amount:</span>
              <span className="font-medium">{invoiceService.formatCurrency(invoice.total_amount)}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Amount Paid:</span>
              <span className="font-medium text-green-600">{invoiceService.formatCurrency(invoice.paid_amount)}</span>
            </div>
            <div className="flex justify-between items-center border-t pt-2">
              <span className="text-sm font-medium text-gray-900">Balance Due:</span>
              <span className="font-bold text-red-600">{invoiceService.formatCurrency(balanceDue)}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Payment Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Amount *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={paymentData.amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  className="block w-full pl-10 pr-12 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="0.00"
                  required
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={setFullAmount}
                    className="text-xs text-yellow-600 hover:text-yellow-700"
                    disabled={loading}
                  >
                    Full
                  </button>
                </div>
              </div>
            </div>

            {/* Payment Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Date *
              </label>
              <input
                type="date"
                value={paymentData.payment_date}
                onChange={(e) => setPaymentData(prev => ({ ...prev, payment_date: e.target.value }))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                required
              />
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method
              </label>
              <select
                value={paymentData.payment_method}
                onChange={(e) => setPaymentData(prev => ({
                  ...prev,
                  payment_method: e.target.value,
                  trust_account_id: e.target.value === 'trust_funds' ? prev.trust_account_id : ''
                }))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
              >
                <option value="">Select method</option>
                <option value="check">Check</option>
                <option value="wire">Wire Transfer</option>
                <option value="credit_card">Credit Card</option>
                <option value="ach">ACH Transfer</option>
                <option value="trust_funds">Trust Funds</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Trust Account Selector */}
            {paymentData.payment_method === 'trust_funds' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trust Account *
                </label>
                {trustAccounts.length > 0 ? (
                  <select
                    value={paymentData.trust_account_id}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, trust_account_id: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                    required
                  >
                    <option value="">Select trust account</option>
                    {trustAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.account_name} — Balance: {invoiceService.formatCurrency(account.balance)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">No trust accounts found for this client. Create a trust account first.</p>
                  </div>
                )}
              </div>
            )}

            {/* Reference Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reference Number
              </label>
              <input
                type="text"
                value={paymentData.reference_number}
                onChange={(e) => setPaymentData(prev => ({ ...prev, reference_number: e.target.value }))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                placeholder="Check number, transaction ID, etc."
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={paymentData.notes}
                onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                placeholder="Additional notes about this payment..."
              />
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center text-red-800">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  <span className="text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Footer Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !paymentData.amount}
                className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Recording...
                  </>
                ) : (
                  'Record Payment'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPaymentModal;
